import { __awaiter, __decorate, __param } from "tslib";
import { observable, volatile } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import { BehaviorSubject } from 'rxjs';
import { Connect } from '../connect';
import { EventMessageType, MessageBuilder, MessageCode, MessageType, } from '../connect/message';
import { CredentialManager } from '../credentialManager';
import { Session } from '../session';
import { CurrentUser } from '../user';
import { logger } from '../utils';
import { AuthError } from './auth.types';
/**
 * The default implementation for the Auth interface.
 * @public
 */
let DefaultAuth = class DefaultAuth {
    loggedUserResultChanged(_, updated) {
        var _a;
        (_a = this.isLoggedInSubject) === null || _a === void 0 ? void 0 : _a.next(this.isLoggedIn);
    }
    get isLoggedIn$() {
        return this.isLoggedInSubject.asObservable();
    }
    get isLoggedIn() {
        return this.loggedUserResult
            ? !!(this.loggedUserResult.authToken && this.loggedUserResult.sessionId)
            : false;
    }
    constructor(session, connect, messageBuilder, credentialManager) {
        this.session = session;
        this.connect = connect;
        this.messageBuilder = messageBuilder;
        this.credentialManager = credentialManager;
        this.isWorking = false;
        this.isLoggedInSubject = new BehaviorSubject(false);
    }
    login(credentials) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.isWorking = true;
            try {
                const loginMessage = this.messageBuilder.createLoginMessage(credentials);
                const authResult = yield this.connect.send(loginMessage);
                if (!authResult || authResult.ERROR || ((_a = authResult.DETAILS) === null || _a === void 0 ? void 0 : _a.ERROR)) {
                    throw new AuthError(loginMessage, authResult);
                }
                this.applyAuthResults({
                    authToken: authResult.SESSION_AUTH_TOKEN,
                    refreshToken: authResult.REFRESH_AUTH_TOKEN,
                    profiles: authResult.PROFILE,
                    username: authResult.USER_NAME,
                    permissions: authResult.PERMISSION,
                    sessionId: authResult.SESSION_ID,
                });
                /**
                 * Use the credentialManager as a proxy instead?
                 */
                this.session.setAuthResult(authResult);
                logger.debug('Login Authentication Result:', authResult);
            }
            catch (err) {
                logger.error('Login Authentication Error:', err);
                if (err instanceof AuthError && err.isIgnorableError()) {
                    logger.warn(`Ignoring authentication error as it is a known edge case.`);
                }
                else {
                    // this.credentialManager.removeAll(); // < let caller manage for now, discuss later
                    this.removeAuthResults();
                    throw err;
                }
            }
            finally {
                this.isWorking = false;
            }
            return this.loggedUserResult;
        });
    }
    logout() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.isWorking = true;
            let logoutResult = {
                success: false,
            };
            try {
                const logoutMessage = this.messageBuilder.createLogoutMessage(this.loggedUserResult);
                // TODO: Improve auth scenario where server timeout has the same message return as logout
                this.session.deleteUserFromSessionStorage();
                this.connect.setValidSession(false);
                const { CODE, DETAILS, ERROR, MESSAGE_TYPE, OIDC_LOGOUT_URL } = yield this.connect.send(logoutMessage);
                const error = (_a = DETAILS === null || DETAILS === void 0 ? void 0 : DETAILS.ERROR) !== null && _a !== void 0 ? _a : ERROR;
                /**
                 * FUI-1109: The server is sending an unexpected logout nack which stops the user from being able to log out.
                 */
                const unexpectedLogoutNack = MESSAGE_TYPE === MessageType.LOGOUT_NACK;
                if (unexpectedLogoutNack) {
                    logger.warn('Unexpected Logout NACK received during logout.', error);
                }
                logoutResult = {
                    success: MESSAGE_TYPE === MessageType.LOGOUT_ACK ||
                        CODE === MessageCode.SESSION_NOT_FOUND ||
                        unexpectedLogoutNack,
                    error,
                };
                // redirect to OIDC provider logout page if provided
                if (OIDC_LOGOUT_URL) {
                    window.open(OIDC_LOGOUT_URL, '_self');
                }
            }
            catch (err) {
                const logoutError = new AuthError(err.sentMessage, err.receivedMessage);
                logger.error('Logout Authentication Error:', logoutError);
                if (logoutError.isIgnorableError()) {
                    logger.warn(`Ignoring authentication error as it is a known edge case.`);
                    logoutResult.success = true;
                }
                else {
                    throw err;
                }
            }
            finally {
                this.removeAuthResults();
                this.isWorking = false;
            }
            return logoutResult;
        });
    }
    reAuthFromSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const isConnected = yield this.ensureConnection();
            if (isConnected) {
                const refreshTokenMessage = this.messageBuilder.createRefreshTokenMessage();
                const refreshTokenResult = yield this.connect.send(refreshTokenMessage);
                if ((refreshTokenResult === null || refreshTokenResult === void 0 ? void 0 : refreshTokenResult.MESSAGE_TYPE) === EventMessageType.EVENT_LOGIN_AUTH_ACK) {
                    this.session.setAuthResult(refreshTokenResult);
                    this.connect.setValidSession(true);
                    this.applyAuthResults(this.getLoggedUserFromMessage(refreshTokenResult));
                    return true;
                }
            }
            this.session.deleteUserFromSessionStorage();
            this.connect.setValidSession(false);
            return false;
        });
    }
    /**
     * Ensures that the connection is active.
     * @internal
     */
    ensureConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connect.isConnected) {
                return true;
            }
            const hostUrl = this.session.getSessionStorageItem('hostUrl');
            if (!hostUrl) {
                return false;
            }
            return this.connect.connect(hostUrl);
        });
    }
    /**
     * Gets the logged user from a Message result.
     * @param result - The Message result from auth request.
     * @returns The logged user info or null if the result is invalid.
     */
    getLoggedUserFromMessage(result) {
        const username = result.USER_NAME;
        const sessionAuthToken = result.SESSION_AUTH_TOKEN;
        const refreshAuthToken = result.REFRESH_AUTH_TOKEN;
        if (!username || !sessionAuthToken || !refreshAuthToken) {
            return null;
        }
        return {
            username,
            authToken: sessionAuthToken,
            refreshToken: refreshAuthToken,
            profiles: result.PROFILE,
            permissions: result.PERMISSION,
            sessionId: result.SESSION_ID,
        };
    }
    /**
     * Applies the authentication results to the Auth instance.
     * @internal
     */
    applyAuthResults(result) {
        if (!result) {
            this.removeAuthResults();
            return;
        }
        this.loggedUserResult = {
            authToken: result.authToken,
            refreshToken: result.refreshToken,
            profiles: result.profiles,
            username: result.username,
            permissions: result.permissions,
            sessionId: result.sessionId,
        };
        this.currentUser = new CurrentUser({
            permissions: result.permissions,
            profiles: result.profiles,
            username: result.username,
        });
    }
    /**
     * Removes the authentication results from the Auth instance.
     * @internal
     */
    removeAuthResults() {
        this.loggedUserResult = null;
        this.currentUser = null;
    }
};
__decorate([
    observable
], DefaultAuth.prototype, "isWorking", void 0);
__decorate([
    observable
], DefaultAuth.prototype, "currentUser", void 0);
__decorate([
    observable
], DefaultAuth.prototype, "loggedUserResult", void 0);
__decorate([
    volatile
], DefaultAuth.prototype, "isLoggedIn", null);
DefaultAuth = __decorate([
    __param(0, Session),
    __param(1, Connect),
    __param(2, MessageBuilder),
    __param(3, CredentialManager)
], DefaultAuth);
export { DefaultAuth };
/**
 * The DI token for the Auth interface.
 * @internal
 */
export const Auth = DI.createInterface((x) => x.singleton(DefaultAuth));
